/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import { isBrowser, isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { currencies, exchange } from '../../../../utilities/constants';
import { formatNumber } from '../../../../utilities/formatHelpers';
import { USER_PREFERENCES } from '../../../../utilities/sharedQueries';
import { BANK_REQUESTS_COINS } from '../../../Accounts/constants';
import {
  useCardStatus,
  useUserCardProvider,
  useUserPersonalInfo,
} from '../../../../utilities/sharedHooks/user';
import { cardProviders } from '../../constants';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import { dynamicAssets } from '../../../../utilities/helpers';

const WalletItem = ({ wallet, onClick, dragProps, isDragging, fiatCurrencyCode }) => {
  const {
    available_balance: availableBalance,
    rate,
    coin,
    currency,
    is_cryptodraft: isCryptodraftWallet,
  } = wallet;

  const balanceInFiat = formatNumber(
    availableBalance * currencies[coin.toUpperCase()].conversion * rate,
    exchange.FIAT_VALUE
  );

  const { isUS } = useUserPersonalInfo();

  const isDepositHidden = useMemo(
    () => process.env.REACT_APP_DEPOSIT_BUTTON_HIDDEN && !isUS && !['usdc', 'euroc'].includes(coin),
    [coin, isUS]
  );

  const { data: cardStatus } = useCardStatus();

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const { cardProvider } = useUserCardProvider();

  const fiatCurrency = useMemo(() => {
    if (cardProvider === cardProviders.MONAVATE && cardStatus?.displayCurrency) {
      return cardStatus?.displayCurrency;
    }
    return userPref?.displayCurrency;
  }, [cardProvider, cardStatus, userPref?.displayCurrency]);

  const userFiatCurrencyCode = fiatCurrencyCode || fiatCurrency || 'EUR';

  const principalCurrency = useMemo(() => {
    if (fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.EUR) {
      return BANK_REQUESTS_COINS.EUROC;
    }
    if (
      fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.USD ||
      (fiatCurrency?.toLowerCase() === BANK_REQUESTS_COINS.GBP &&
        cardProvider === cardProviders.MONAVATE)
    ) {
      return BANK_REQUESTS_COINS.USDT;
    }
    return fiatCurrency;
  }, [fiatCurrency, cardProvider]);

  const displayCurrencyRate = useRate(coin, principalCurrency);

  const { t: translate } = useTranslation();

  const coinDetails = isCryptodraftWallet ? (
    <div className="d-flex flex-row align-items-center">
      <img
        alt={coin}
        className="m-0 me-3"
        src={dynamicAssets('./assets/icons/cryptodraft.svg')}
        style={{ width: '2rem', height: '2rem' }}
      />
      <b className="text-f5 text-dark">{translate('cryptodraft_spending_wallet_name')}</b>
    </div>
  ) : (
    <div className="d-flex flex-row align-items-center">
      <img
        alt={coin}
        className="m-0 me-3"
        src={dynamicAssets(`./assets/icons/${coin.toUpperCase()}.svg`)}
        style={{
          width: '2rem',
          height: '2rem',
          ...(wallet?.coin?.toUpperCase() === currencies.XRP.icon
            ? {
                backgroundColor: 'white',
                padding: '1px',
                borderRadius: '20px',
              }
            : {}),
        }}
      />
      <div>
        <b className="text-f5 text-dark">{coin.toUpperCase()}</b>
        {isBrowser && <p className="text-shadow text-f6">{currency.fullName}</p>}
        {isMobile && (
          <p className="text-shadow text-f6">
            {balanceInFiat} {userFiatCurrencyCode.toUpperCase()}
          </p>
        )}
      </div>
    </div>
  );

  const availableBalanceValue = useMemo(() => {
    if (isCryptodraftWallet && cardProvider === cardProviders.MONAVATE) {
      const cryptodraftAvailableBalance =
        availableBalance * currencies[coin.toUpperCase()].conversion * displayCurrencyRate;

      return cryptodraftAvailableBalance > 0 ? cryptodraftAvailableBalance : '0.00';
    }
    return availableBalance * currencies[coin.toUpperCase()].conversion;
  }, [availableBalance, isCryptodraftWallet, coin, displayCurrencyRate, cardProvider]);

  const balanceDetails = (
    <div className="px-0">
      <b className="text-f5 text-dark">
        {`${formatNumber(availableBalanceValue, currency.decimal)} ${
          !isCryptodraftWallet ? coin?.toUpperCase() : principalCurrency?.toUpperCase()
        }`}
      </b>
      {!isCryptodraftWallet && (
        <p className="text-shadow text-f6">
          {`${balanceInFiat} ${userFiatCurrencyCode.toUpperCase()}`}
        </p>
      )}
    </div>
  );

  const actions = (
    <div className="d-flex flex-row align-items-center justify-content-end">
      {!isDepositHidden && (
        <button
          type="button"
          className={`btn text-primary text-f6 p-0 d-xl-block text-btn-item ${
            isMobile ? 'd-none' : ''
          }`}
          style={{ marginRight: '1.25rem' }}
          onClick={() => {
            onClick();
          }}>
          <b>{translate('add_funds_btn_title')}</b>
        </button>
      )}
      <div className="text-shadow" style={{ width: '1.5rem', height: '1.5rem' }}>
        <i
          {...dragProps.dragHandleProps}
          className="icon-dragdrop"
          style={{ fontSize: '1.5rem' }}
        />
      </div>
    </div>
  );
  return (
    <div
      role={isBrowser ? 'none' : 'button'}
      tabIndex={-1}
      onKeyDown={isBrowser ? null : onClick}
      onClick={isBrowser ? null : onClick}
      ref={dragProps.innerRef}
      {...dragProps.draggableProps}
      className={`d-flex bg-lighter rounded-extra py-3 px-xl-4 px-3 my-3 hover-item ${
        isDragging ? 'border-primary' : ''
      } ${isCryptodraftWallet ? 'py-4' : ''}`}
      style={{ userSelect: 'none', ...dragProps.draggableProps.style }}>
      <div className="col-sm-4 col-6 my-auto">{coinDetails}</div>
      <div className="col-4 d-sm-block d-none my-auto">{balanceDetails}</div>
      <div className="col-sm-4 col-6 my-auto pe-0">{actions}</div>
    </div>
  );
};

export default WalletItem;

WalletItem.propTypes = {
  wallet: PropTypes.object.isRequired,
  isDragging: PropTypes.bool.isRequired,
  dragProps: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  fiatCurrencyCode: PropTypes.string,
};

WalletItem.defaultProps = {
  fiatCurrencyCode: null,
};
